import { Button, FlexRow, Modal, SearchBox } from 'components';
import { handleApiErrorWithNotification } from 'helpers/errorHandling';
import { useCustomTranslation } from 'helpers/translationHelper';
import type { Material, SelectedMaterialItem } from 'pages/material/models';
import { useAddMaterialToStoreMutation } from 'pages/material/queries';
import React, { useState } from 'react';

import type { EditAmountFormValues, MaterialToEditAmount } from '../EditAmountForm';
import EditAmountForm from '../EditAmountForm';
import MaterialList from './MaterialList';
import MaterialToMoveList from './MaterialToMoveList';

interface Props {
  open: boolean;
  onClose: () => void;
  selectedStoreId: string;
  storeName: string;
}

const MoveMaterialToStoreModal: React.FC<Props> = (props): React.ReactElement => {
  const { onClose, open, selectedStoreId, storeName } = props;

  const { t } = useCustomTranslation();

  const addMaterialToStoreMutation = useAddMaterialToStoreMutation();

  const [searchPhrase, setSearchPhrase] = useState<string | undefined>();
  const [selectedMaterial, setSelectedMaterial] = useState<SelectedMaterialItem[]>([]);
  const [itemToEdit, setItemToEdit] = useState<MaterialToEditAmount | undefined>();
  const [isMoving, setIsMoving] = useState(false);

  const removeMaterialFromSelected = (materialId: string) => {
    setSelectedMaterial(selectedMaterial.filter((x) => x.materialId !== materialId));
  };

  const setMaterialAmount = (material: Material, amount?: number | null) => {
    const selectedMaterialItem = selectedMaterial.find((x) => x.materialId === material.id);

    if (selectedMaterialItem && amount !== 0) {
      setSelectedMaterial(
        selectedMaterial.map((x) => {
          if (x.materialId === selectedMaterialItem.materialId) {
            return {
              ...selectedMaterialItem,
              amount
            };
          }
          return x;
        })
      );
    } else if (selectedMaterialItem) {
      removeMaterialFromSelected(material.id);
    } else {
      const materialToStore: SelectedMaterialItem = {
        name: material.name,
        unit: material.unit,
        amount,
        materialId: material.id
      };

      setSelectedMaterial([...selectedMaterial, materialToStore]);
    }
  };

  const handleEditAmount = (values: EditAmountFormValues) => {
    if (values.amount === 0) {
      removeMaterialFromSelected(itemToEdit?.materialId || '');
    } else {
      setSelectedMaterial(
        selectedMaterial.map((material) => {
          if (material.materialId === itemToEdit?.materialId) {
            return {
              ...material,
              amount: values.amount
            };
          }

          return material;
        })
      );
    }

    setItemToEdit(undefined);
  };

  const handleClose = () => {
    if (itemToEdit) {
      return setItemToEdit(undefined);
    }

    return onClose();
  };

  const handleMove = async () => {
    setIsMoving(true);
    try {
      await addMaterialToStoreMutation.mutateAsync({
        storeId: selectedStoreId,
        data: selectedMaterial
      });
    } catch (e) {
      handleApiErrorWithNotification(e);
    } finally {
      setIsMoving(false);
      onClose();
    }
  };

  const getTitle = () => {
    if (itemToEdit) {
      return t('stores.editMaterialAmountTitle');
    }

    return t('stores.incomeMaterialTitle', {
      value: storeName
    });
  };

  const getContent = () => {
    if (itemToEdit) {
      return (
        <EditAmountForm
          item={itemToEdit}
          onCancel={() => setItemToEdit(undefined)}
          onSubmit={handleEditAmount}
          allText={t('stores.allAmountLabel')}
        />
      );
    }

    return (
      <div className="MaterialOperationModal_content">
        <MaterialList
          searchPhrase={searchPhrase}
          selectedMaterial={selectedMaterial}
          setMaterialAmount={setMaterialAmount}
        />
        <MaterialToMoveList
          items={selectedMaterial}
          onDelete={removeMaterialFromSelected}
          onEdit={setItemToEdit}
        />
      </div>
    );
  };

  return (
    <Modal
      width={900}
      title={getTitle()}
      actions={
        !itemToEdit && (
          <FlexRow noMargin>
            <SearchBox onValueChanged={setSearchPhrase} />
          </FlexRow>
        )
      }
      onClose={handleClose}
      open={open}
      footer={
        !itemToEdit && (
          <FlexRow noMargin>
            <Button onClick={() => onClose()}>{t('common.cancel')}</Button>
            <Button onClick={() => handleMove()} type="primary" loading={isMoving}>
              {t('common.save')}
            </Button>
          </FlexRow>
        )
      }
      className="MaterialOperationModal"
      surviveClose={false}
    >
      {getContent()}
    </Modal>
  );
};

export default MoveMaterialToStoreModal;
